// i18n translations dictionary
// Languages: English (en) - default, Arabic (ar)

export type Locale = 'en' | 'ar' | 'zh';

export const locales: Locale[] = ['en', 'ar', 'zh'];

export const localeNames: Record<Locale, string> = {
  en: 'English',
  ar: 'العربية',
  zh: '中文',
};

export const isRtl = (locale: Locale): boolean => locale === 'ar';

// Deep nested translation object
export const translations = {
  en: {
    nav: {
      home: 'Home',
      about: 'About Us',
      services: 'Services',
      successStories: 'Success Stories',
      blog: 'Blog',
      resources: 'Resources',
      wholesale: 'Wholesale',
      batterySolution: 'Battery Solution',
      faq: 'FAQ',
      contact: 'Contact',
      getQuote: 'Get a Quote',
    },
    common: {
      learnMore: 'Learn More',
      getStarted: 'Get Started',
      contactUs: 'Contact Us',
      download: 'Download',
      readMore: 'Read More',
      viewAll: 'View All',
      submit: 'Submit',
      sending: 'Sending...',
      loading: 'Loading...',
      search: 'Search',
      all: 'All',
      filter: 'Filter',
      share: 'Share',
      back: 'Back',
      next: 'Next',
      previous: 'Previous',
      featured: 'Featured',
      free: 'Free',
      required: 'Required',
      name: 'Name',
      email: 'Email',
      company: 'Company',
      message: 'Message',
      phone: 'Phone',
      country: 'Country',
      city: 'City',
      requiredField: '* Required',
      readTime: 'min read',
    },
    footer: {
      description:
        'Empowering thousands of garages to transform to new energy. China-based full-stack NEV after-sales enablement service provider.',
      servicesTitle: 'Services',
      companyTitle: 'About WFD',
      contactTitle: 'Contact',
      b2bNote: 'B2B Only — We work exclusively with businesses',
      rights: 'All rights reserved.',
      privacy: 'Privacy Policy',
      terms: 'Terms of Service',
    },
    hero: {
      subtitle: 'Empower Your Garage to Master NEV',
      ctaPrimary: 'Start Your EV Journey',
      ctaSecondary: 'View Services',
    },
    contact: {
      title: 'Get in Touch',
      subtitle: 'Reach out to our team. We typically respond within 24 business hours.',
      namePlaceholder: 'Your full name',
      emailPlaceholder: 'you@company.com',
      companyPlaceholder: 'Your company name',
      cityPlaceholder: 'City / Country',
      whatsappPlaceholder: 'WhatsApp number',
      volumePlaceholder: 'Estimated monthly volume',
      interestedLabel: 'Interested In',
      messageLabel: 'Your Message',
      messagePlaceholder: 'Tell us about your requirements...',
      submitButton: 'Send Inquiry',
      successTitle: 'Message Sent!',
      successDesc:
        'Thank you for reaching out. Our team has received your inquiry and will get back to you within 24 business hours.',
      sendAnother: 'Send Another Message',
    },
    blog: {
      eyebrow: 'Blog & Insights',
      title: 'Expert Insights on the EV After-Sales Industry',
      description:
        'Technical deep-dives, market analysis, and practical guides for garage owners, technicians, and NEV industry professionals.',
      featuredTitle: 'Featured Articles',
      allTitle: 'Explore Our Library',
      searchPlaceholder: 'Search articles...',
      noResults: 'No articles found',
      noResultsDesc: 'Try adjusting your search or category filter.',
      readArticle: 'Read Article',
      related: 'Related Reading',
      youMayLike: 'You May Also Like',
      backToBlog: 'Back to Blog',
      shareThis: 'Share this article',
      prev: 'Previous Article',
      next: 'Next Article',
      tags: 'Tags',
      by: 'By',
    },
    resources: {
      eyebrow: 'Free Resources',
      title: 'Expert Knowledge, Delivered to Your Inbox',
      description:
        'Download free whitepapers, market reports, case studies, and practical guides. All based on real-world experience from 1000+ garages we\'ve empowered.',
      allTitle: 'Free Downloads Available',
      allDesc:
        'All resources are free — we just ask for your business email so we can send you the download link and keep you updated on new resources.',
      downloadFree: 'Download Free',
      formTitle: 'Get Your Free Copy',
      formDesc: 'Fill in your details below to receive the download link in your email.',
      formButton: 'Send Download Link',
      formNote:
        'By downloading, you agree to receive occasional updates from WFD New Energy. Unsubscribe anytime.',
      successTitle: 'Thank You!',
      successDesc:
        'The download link has been sent to your email. Please check your inbox.',
      successNote: 'Don\'t see it? Check your spam folder or contact us at',
      pages: 'pages',
      highlights: 'What you\'ll get',
      for: 'For:',
    },
    floatingEmail: {
      title: 'Quick Email Us',
      subtitle: 'We reply within 24 hours',
      name: 'Name',
      email: 'Email',
      company: 'Company',
      city: 'City / Country',
      interested: 'Interested In',
      message: 'Message',
      send: 'Send Message',
      sending: 'Sending...',
      orEmail: 'Or email us directly at',
      successTitle: 'Message Sent!',
      successDesc: 'Thank you! Our team will get back to you within 24 business hours.',
      close: 'Close',
    },
    cta: {
      title: 'Ready to Transform Your NEV Business?',
      description:
        'Join 1000+ garages and distributors who have already partnered with WFD New Energy. Let\'s build your success story together.',
      button: 'Contact Us Today',
    },
  },

  ar: {
    nav: {
      home: 'الرئيسية',
      about: 'من نحن',
      services: 'خدماتنا',
      successStories: 'قصص نجاح',
      blog: 'المدونة',
      resources: 'الموارد',
      wholesale: 'الجملة',
      batterySolution: 'حلول البطاريات',
      faq: 'الأسئلة الشائعة',
      contact: 'اتصل بنا',
      getQuote: 'احصل على عرض',
    },
    common: {
      learnMore: 'اعرف المزيد',
      getStarted: 'ابدأ الآن',
      contactUs: 'اتصل بنا',
      download: 'تحميل',
      readMore: 'اقرأ المزيد',
      viewAll: 'عرض الكل',
      submit: 'إرسال',
      sending: 'جاري الإرسال...',
      loading: 'جاري التحميل...',
      search: 'بحث',
      all: 'الكل',
      filter: 'تصفية',
      share: 'مشاركة',
      back: 'رجوع',
      next: 'التالي',
      previous: 'السابق',
      featured: 'مميز',
      free: 'مجاني',
      required: 'مطلوب',
      name: 'الاسم',
      email: 'البريد الإلكتروني',
      company: 'الشركة',
      message: 'الرسالة',
      phone: 'الهاتف',
      country: 'البلد',
      city: 'المدينة',
      requiredField: '* مطلوب',
      readTime: 'دقيقة قراءة',
    },
    footer: {
      description:
        'نمكّن آلاف ورش العمل من التحول إلى الطاقة الجديدة. مزود خدمات متكامل لما بعد البيع لمركبات الطاقة الجديدة من الصين.',
      servicesTitle: 'الخدمات',
      companyTitle: 'عن WFD',
      contactTitle: 'تواصل معنا',
      b2bNote: 'للشركات فقط — نعمل حصراً مع الشركات',
      rights: 'جميع الحقوق محفوظة.',
      privacy: 'سياسة الخصوصية',
      terms: 'شروط الخدمة',
    },
    hero: {
      subtitle: 'مكّن ورشتك من إتقان مركبات الطاقة الجديدة',
      ctaPrimary: 'ابدأ رحلتك في EV',
      ctaSecondary: 'عرض الخدمات',
    },
    contact: {
      title: 'تواصل معنا',
      subtitle: 'تواصل مع فريقنا. نرد عادةً خلال 24 ساعة عمل.',
      namePlaceholder: 'اسمك الكامل',
      emailPlaceholder: 'you@company.com',
      companyPlaceholder: 'اسم شركتك',
      cityPlaceholder: 'المدينة / البلد',
      whatsappPlaceholder: 'رقم واتساب',
      volumePlaceholder: 'الحجم الشهري المتوقع',
      interestedLabel: 'مهتم بـ',
      messageLabel: 'رسالتك',
      messagePlaceholder: 'أخبرنا عن متطلباتك...',
      submitButton: 'إرسال الاستفسار',
      successTitle: 'تم إرسال الرسالة!',
      successDesc:
        'شكراً لتواصلك. تلقى فريقنا استفسارك وسيرد عليك خلال 24 ساعة عمل.',
      sendAnother: 'إرسال رسالة أخرى',
    },
    blog: {
      eyebrow: 'المدونة والرؤى',
      title: 'رؤى متخصصة في صناعة ما بعد البيع لمركبات EV',
      description:
        'تحليلات تقنية متعمقة وتحليلات سوقية وأدلة عملية لأصحاب ورش العمل والفنيين ومهنيي صناعة مركبات الطاقة الجديدة.',
      featuredTitle: 'مقالات مميزة',
      allTitle: 'استكشف مكتبتنا',
      searchPlaceholder: 'البحث في المقالات...',
      noResults: 'لم يتم العثور على مقالات',
      noResultsDesc: 'حاول تعديل البحث أو تصنيف التصفية.',
      readArticle: 'اقرأ المقال',
      related: 'قراءات ذات صلة',
      youMayLike: 'قد يعجبك أيضاً',
      backToBlog: 'العودة للمدونة',
      shareThis: 'مشاركة هذا المقال',
      prev: 'المقال السابق',
      next: 'المقال التالي',
      tags: 'الوسوم',
      by: 'بقلم',
    },
    resources: {
      eyebrow: 'موارد مجانية',
      title: 'معرفة متخصصة تصل إلى بريدك',
      description:
        'قم بتنزيل أوراق بيضاء وتقارير سوقية وقصص نجاح وأدلة عملية مجانية. كلها تستند إلى تجربة حقيقية من أكثر من 1000 ورشة عمل قمنا بتمكينها.',
      allTitle: 'تحميلات مجانية متاحة',
      allDesc:
        'جميع الموارد مجانية — نطلب فقط بريدك الإلكتروني التجاري لنرسل لك رابط التحميل ونطلعك على الموارد الجديدة.',
      downloadFree: 'تحميل مجاني',
      formTitle: 'احصل على نسختك المجانية',
      formDesc: 'املأ بياناتك أدناه لتلقي رابط التحميل في بريدك الإلكتروني.',
      formButton: 'إرسال رابط التحميل',
      formNote:
        'بالتنزيل، أنت توافق على تلقي تحديثات عرضية من WFD New Energy. يمكنك إلغاء الاشتراك في أي وقت.',
      successTitle: 'شكراً لك!',
      successDesc:
        'تم إرسال رابط التحميل إلى بريدك الإلكتروني. يرجى التحقق من صندوق الوارد.',
      successNote: 'لم تجده؟ تحقق من مجلد البريد العشوائي أو اتصل بنا على',
      pages: 'صفحة',
      highlights: 'ما ستحصل عليه',
      for: 'لـ:',
    },
    floatingEmail: {
      title: 'راسلنا بسرعة',
      subtitle: 'نرد خلال 24 ساعة',
      name: 'الاسم',
      email: 'البريد',
      company: 'الشركة',
      city: 'المدينة / البلد',
      interested: 'مهتم بـ',
      message: 'الرسالة',
      send: 'إرسال الرسالة',
      sending: 'جاري الإرسال...',
      orEmail: 'أو راسلنا مباشرة على',
      successTitle: 'تم الإرسال!',
      successDesc: 'شكراً لك! سيتواصل فريقنا معك خلال 24 ساعة عمل.',
      close: 'إغلاق',
    },
    cta: {
      title: 'مستعد لتحويل عملك في مركبات الطاقة الجديدة؟',
      description:
        'انضم إلى أكثر من 1000 ورش عمل وموزع شريك مع WFD New Energy. لنبني قصة نجاحك معاً.',
      button: 'اتصل بنا اليوم',
    },
  },
  zh: {
    nav: {
      home: '首页',
      about: '关于我们',
      services: '核心服务',
      successStories: '成功案例',
      blog: '博客资讯',
      resources: '资源中心',
      wholesale: '批发合作',
      batterySolution: '电池方案',
      faq: '常见问题',
      contact: '联系我们',
      getQuote: '获取报价',
    },
    common: {
      learnMore: '了解更多',
      getStarted: '立即开始',
      contactUs: '联系我们',
      download: '下载',
      readMore: '阅读更多',
      viewAll: '查看全部',
      submit: '提交',
      sending: '发送中...',
      loading: '加载中...',
      search: '搜索',
      all: '全部',
      filter: '筛选',
      share: '分享',
      back: '返回',
      next: '下一页',
      previous: '上一页',
      featured: '精选',
      free: '免费',
      required: '必填',
      name: '姓名',
      email: '邮箱',
      company: '公司',
      message: '留言',
      phone: '电话',
      country: '国家',
      city: '城市',
      requiredField: '* 必填项',
      readTime: '分钟阅读',
    },
    footer: {
      description:
        '赋能千家修理厂完成新能源转型。中国新能源汽车后市场全产业链赋能服务商。',
      servicesTitle: '核心服务',
      companyTitle: '关于 WFD',
      contactTitle: '联系方式',
      b2bNote: '仅限企业合作 — 我们只服务于企业客户',
      rights: '版权所有',
      privacy: '隐私政策',
      terms: '服务条款',
    },
    hero: {
      subtitle: '助力您的修理厂掌握新能源技术',
      ctaPrimary: '开启新能源之旅',
      ctaSecondary: '查看服务',
    },
    contact: {
      title: '联系我们',
      subtitle: '与我们的团队取得联系，通常在 24 个工作小时内回复。',
      namePlaceholder: '您的全名',
      emailPlaceholder: 'you@company.com',
      companyPlaceholder: '您的公司名称',
      cityPlaceholder: '城市 / 国家',
      whatsappPlaceholder: 'WhatsApp 号码',
      volumePlaceholder: '预估月需求量',
      interestedLabel: '感兴趣的方向',
      messageLabel: '您的留言',
      messagePlaceholder: '告诉我们您的需求...',
      submitButton: '发送询盘',
      successTitle: '消息已发送！',
      successDesc:
        '感谢您的联系，我们的团队已收到您的询盘，将在 24 个工作小时内回复您。',
      sendAnother: '再发一条消息',
    },
    blog: {
      eyebrow: '博客与洞察',
      title: '新能源汽车后市场行业专家洞察',
      description:
        '面向修理厂老板、技术人员和新能源行业从业者的技术深度解析、市场分析与实操指南。',
      featuredTitle: '精选文章',
      allTitle: '探索我们的知识库',
      searchPlaceholder: '搜索文章...',
      noResults: '未找到相关文章',
      noResultsDesc: '请尝试调整搜索关键词或分类筛选。',
      readArticle: '阅读全文',
      related: '相关阅读',
      youMayLike: '你可能也喜欢',
      backToBlog: '返回博客',
      shareThis: '分享本文',
      prev: '上一篇',
      next: '下一篇',
      tags: '标签',
      by: '作者',
    },
    resources: {
      eyebrow: '免费资源',
      title: '专业知识，直达您的邮箱',
      description:
        '免费下载白皮书、市场报告、案例研究和实操指南。全部基于我们赋能 1000+ 修理厂的真实经验。',
      allTitle: '可免费下载的资源',
      allDesc:
        '所有资源均免费 — 只需留下您的企业邮箱，我们将把下载链接发送给您，并定期推送新资源。',
      downloadFree: '免费下载',
      formTitle: '获取免费资源',
      formDesc: '填写以下信息，下载链接将发送到您的邮箱。',
      formButton: '发送下载链接',
      formNote:
        '下载即表示您同意接收 WFD 新能源的不定期更新，可随时取消订阅。',
      successTitle: '感谢您！',
      successDesc: '下载链接已发送到您的邮箱，请注意查收。',
      successNote: '没收到？请检查垃圾邮件文件夹，或通过以下邮箱联系我们：',
      pages: '页',
      highlights: '您将获得',
      for: '适用人群：',
    },
    floatingEmail: {
      title: '快速邮件咨询',
      subtitle: '24 小时内回复',
      name: '姓名',
      email: '邮箱',
      company: '公司',
      city: '城市 / 国家',
      interested: '感兴趣的方向',
      message: '留言',
      send: '发送消息',
      sending: '发送中...',
      orEmail: '或直接发送邮件到',
      successTitle: '消息已发送！',
      successDesc: '感谢您！我们的团队将在 24 个工作小时内与您联系。',
      close: '关闭',
    },
    cta: {
      title: '准备好转型新能源业务了吗？',
      description:
        '加入 1000+ 家已与 WFD 新能源合作的修理厂和经销商，让我们一起打造您的成功故事。',
      button: '立即联系我们',
    },
  },
} as const;

export type Translations = (typeof translations)['en'];

// Helper to ensure all locales have the same shape
type _AssertSameShape = _Assert<(typeof translations)['en'], (typeof translations)['ar']> &
  _Assert<(typeof translations)['en'], (typeof translations)['zh']>;
type _Assert<T, U> = T extends U ? (U extends T ? true : never) : never;
